// ============================================================

// Safe date converter (handles Firestore Timestamps)
// VidyaTrack — Helper utilities
// Safe converters for Firestore data
// ============================================================

/**
 * Safely convert a Firestore Timestamp, ISO string, or Date to a JS Date.
 * Returns null if invalid.
 */
export function toDate(value) {
  if (!value) return null;
  // Firestore Timestamp object
  if (typeof value.toDate === "function") return value.toDate();
  // Already a Date
  if (value instanceof Date) return value;
  // ISO string or number
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Safely format a date value — returns empty string if invalid.
 */
export function safeFormat(value, formatStr = "MMM d, yyyy") {
  const d = toDate(value);
  if (!d) return "";
  try {
    const { format } = require("date-fns");
    return format(d, formatStr);
  } catch {
    return "";
  }
}

/**
 * Safely extract a display string from a member (could be string or object).
 */
export function memberName(m) {
  if (!m) return "";
  if (typeof m === "string") return m;
  if (typeof m === "object") return m.name || m.displayName || "";
  return String(m);
}

/**
 * Ensure a value rendered in JSX is always a primitive.
 * Returns "" for objects/arrays/null/undefined.
 */
export function safe(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    // Firestore Timestamp
    if (typeof value.toDate === "function") return value.toDate().toLocaleDateString();
    return "";
  }
  return value;
}

/**
 * Sanitise a Firestore document — convert all Timestamp fields to ISO strings
 * so they are safe to store in React state and render.
 */
export function sanitiseDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const out = {};
  for (const [key, val] of Object.entries(doc)) {
    if (val && typeof val === "object" && typeof val.toDate === "function") {
      out[key] = val.toDate().toISOString();
    } else if (val && typeof val === "object" && !(val instanceof Date) && !Array.isArray(val)) {
      // Nested object — recurse but only one level deep
      out[key] = sanitiseDoc(val);
    } else {
      out[key] = val;
    }
  }
  return out;
}

/**
 * Sanitise an array of Firestore documents.
 */
export function sanitiseDocs(docs) {
  if (!Array.isArray(docs)) return [];
  return docs.map(sanitiseDoc);
}
